import React from "react";
import { motion } from "framer-motion";

import firebase from '../images/firebase.png';
import node from '../images/node.webp';
import mongo from '../images/mongo.png';
import java from '../images/java.svg';
import python from '../images/python.png';
import html from '../images/html.png';
import css from '../images/css.png';
import javascript from '../images/javascript.png';
import react from '../images/react.svg';
import chesshome from '../images/chesshome.png';
import skillora from '../images/skillora.jpg';
import food from '../images/food.jpg';
import street from '../images/streetsence.jpg';
import Navbar from "../components/Navbar";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const servicePackages = [
  {
    name: "Startup Tier",
    description: "Architectural foundation for rapid market entry and validation.",
    features: [
      "Atomic UI Component Architecture",
      "Dynamic Content Management",
      "Core SEO Optimization",
      "Hyper-Responsive Framework",
      "90-Day Engineering Support"
    ],
    technologies: ["React", "Node.js", "MongoDB"],
    timeline: "4-6 weeks",
  },
  {
    name: "Growth Tier",
    description: "High-performance ecosystem engineered for massive scale.",
    features: [
      "Custom Micro-Interactions",
      "Distributed Data Architecture",
      "Integrated FinTech Hub",
      "Intelligence Dashboard",
      "180-Day Engineering Support",
      "Infrastructure Hardening"
    ],
    technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
    timeline: "8-12 weeks",
    popular: true
  },
  {
    name: "Enterprise Tier",
    description: "Mission-critical infrastructure for global dominance.",
    features: [
      "Zero-Trust Security Mesh",
      "Automated DevOps Pipelines",
      "Global Edge Delivery",
      "Predictive Analytics Layer",
      "365-Day Priority Support",
      "Compliance Engineering"
    ],
    technologies: ["React", "Node.js", "Kubernetes", "AWS/Azure"],
    timeline: "16-24 weeks",
  }
];

const technologyStack = [
  {
    category: "Interface Layers",
    technologies: [
      { name: "React", img: react, desc: "Declarative UI" },
      { name: "HTML5", img: html, desc: "Semantic Core" },
      { name: "CSS3", img: css, desc: "Visual Logic" },
      { name: "JS/TS", img: javascript, desc: "Engine Logic" }
    ]
  },
  {
    category: "Backend Systems",
    technologies: [
      { name: "Node.js", img: node, desc: "Runtime Mesh" },
      { name: "Python", img: python, desc: "AI Integration" },
      { name: "Java", img: java, desc: "Enterprise Core" }
    ]
  },
  {
    category: "Data & Cloud",
    technologies: [
      { name: "MongoDB", img: mongo, desc: "NoSQL Fabric" },
      { name: "Firebase", img: firebase, desc: "Real-time Sync" }
    ]
  }
];

export default function WebDevelopment() {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -mr-48 -mt-48 animate-blob" />
      <div className="fixed bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full -ml-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-64 pb-32 px-6 overflow-hidden">
        <div className="container mx-auto max-w-7xl relative z-10">
          <motion.div className="flex flex-col items-center text-center mb-32" {...fadeInUp}>
            <div className="inline-flex items-center gap-3 px-6 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full mb-12 shadow-2xl">
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-[0_0_15px_rgba(59,130,246,0.5)]" />
              <span className="text-[10px] uppercase font-black tracking-[0.3em] text-slate-400">Enterprise Engineering Slot Active</span>
            </div>
            <h1 className="text-5xl md:text-[8rem] lg:text-[10rem] font-black text-white mb-12 tracking-tighter uppercase leading-[0.8] relative px-4">
              Web <br /><span className="text-gradient">Architects.</span>
            </h1>
            <p className="text-lg md:text-2xl lg:text-3xl text-slate-400 max-w-4xl mx-auto mb-20 font-medium lowercase tracking-tight leading-relaxed px-6">
              we build high-fidelity digital infrastructure. scalable, secure, and precision-engineered for global scale and mission-critical performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-8 justify-center">
              <a
                href="https://wa.me/94723954658"
                target="_blank"
                rel="noopener noreferrer"
                className="px-16 py-7 bg-slate-900 text-white font-black rounded-[2.5rem] shadow-2xl hover:bg-black transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-[11px] flex items-center gap-3"
              >
                INITIATE SPRINT
                <span className="text-xl">→</span>
              </a>
              <button
                onClick={() => document.getElementById('packages').scrollIntoView({ behavior: 'smooth' })}
                className="px-16 py-7 bg-white/5 backdrop-blur-xl border border-white/10 text-white font-black rounded-[2.5rem] hover:bg-white/10 transition-all shadow-2xl uppercase tracking-widest text-[11px]"
              >
                EXPLORE TIERS
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tiers Section */}
      <section id="packages" className="py-40 px-6 relative z-10 overflow-hidden bg-[#f1f5f9]/40 border-y border-white/5">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-32">
            <h2 className="text-5xl font-black text-white mb-6 tracking-tighter uppercase">Deployment Tiers</h2>
            <div className="h-1.5 w-24 bg-blue-600 mx-auto rounded-full shadow-lg shadow-blue-500/20" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {servicePackages.map((pkg, idx) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className={`group glass-card p-10 md:p-16 rounded-[3rem] md:rounded-[4.5rem] border-white/10 hover:bg-white/5 transition-all duration-700 hover:shadow-2xl hover:translate-y-[-12px] glow-border relative overflow-hidden ${pkg.popular ? 'ring-2 ring-blue-500/20' : ''}`}
              >
                {pkg.popular && (
                  <div className="absolute top-12 right-12 bg-blue-600 text-white px-6 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none shadow-xl z-20">
                    Optimal Build
                  </div>
                )}
                <h3 className="text-4xl font-black text-white mb-8 tracking-tighter uppercase leading-none">{pkg.name}</h3>
                <p className="text-slate-400 font-medium text-lg mb-14 leading-relaxed lowercase tracking-tight">{pkg.description}</p>
                <div className="space-y-6 mb-16">
                  {pkg.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-6 group/item">
                      <div className="w-2.5 h-2.5 bg-blue-200 border border-blue-600 rounded-full transition-all group-hover/item:bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.2)]" />
                      <span className="text-slate-400 text-[11px] font-black uppercase tracking-widest leading-none">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-12 border-t border-white/5 flex flex-col gap-10">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] uppercase font-black text-slate-400 tracking-[0.3em]">Deployment Cycle</span>
                    <span className="text-sm font-black text-white uppercase tracking-tighter">{pkg.timeline}</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {pkg.technologies.map(t => (
                      <span key={t} className="px-5 py-2.5 bg-[#020617] border border-white/5 rounded-full text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] group-hover:text-blue-600 group-hover:border-blue-100 transition-colors">{t}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stack Section */}
      <section className="py-40 px-6 relative z-10 bg-slate-900/40">
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-32">
            <h2 className="text-5xl font-black text-white mb-6 tracking-tighter uppercase leading-none">The Engine Stack</h2>
            <p className="text-slate-400 font-medium lowercase tracking-tight max-w-sm mx-auto">we deploy only industry-grade engineering frameworks.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-16">
            {technologyStack.map((cat, idx) => (
              <motion.div
                key={cat.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.4em] mb-14 text-center">{cat.category}</h3>
                <div className="space-y-8">
                  {cat.technologies.map(t => (
                    <div key={t.name} className="glass-card p-8 rounded-[3rem] flex items-center gap-8 border border-white hover:bg-[#020617] transition-all duration-500 group shadow-sm hover:shadow-xl hover:shadow-blue-500/5">
                      <div className="w-16 h-16 bg-slate-900/40 rounded-2xl flex items-center justify-center p-4 border border-white/5 group-hover:rotate-12 group-hover:scale-110 transition-all duration-500 shadow-sm">
                        <img src={t.img} alt={t.name} className="w-full h-full object-contain transition-all" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-black text-white text-lg tracking-tighter uppercase leading-none">{t.name}</h4>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">{t.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-40 px-6 relative z-10 bg-[#020617] overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-32">
            <h2 className="text-5xl font-black text-white mb-6 tracking-tighter uppercase leading-none">Featured Builds</h2>
            <p className="text-slate-400 font-medium lowercase tracking-tight max-w-sm mx-auto">High-fidelity web applications delivered to global enterprises.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-12">
            {[
              {
                title: "Ceylon Chess",
                img: chesshome,
                tag: "National Platform",
                desc: "Tournament management platform for the national chess circuit."
              },
              {
                title: "Skillora",
                img: skillora,
                tag: "AI Learning",
                desc: "Comprehensive e-learning platform with AI-driven personalization."
              },
              {
                title: "FoodFlowSL",
                img: food,
                tag: "Management System",
                desc: "Unified platform for restaurant orders and delivery management."
              },
              {
                title: "StreetSense",
                img: street,
                tag: "Smart City",
                desc: "IoT-enabled urban monitoring and management system."
              }
            ].map((project, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative bg-slate-900/40 rounded-[3rem] overflow-hidden border border-white/5 hover:shadow-2xl transition-all duration-700 w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)]"
              >
                <div className="relative h-64 overflow-hidden bg-[#020617]">
                  <img src={project.img} alt={project.title} className="w-full h-full object-contain group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-40 pointer-events-none" />
                  <span className="absolute top-6 left-6 px-4 py-2 bg-white/5 backdrop-blur-md rounded-lg text-[9px] font-black text-white uppercase tracking-widest border border-white/10 shadow-2xl">
                    {project.tag}
                  </span>
                </div>
                <div className="p-10">
                  <h3 className="text-2xl font-black text-white mb-4 tracking-tighter uppercase leading-none">{project.title}</h3>
                  <p className="text-slate-400 text-sm font-medium lowercase tracking-tight">{project.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-48 px-6 relative z-10 bg-[#020617]">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-24 md:p-32 rounded-[5rem] text-center border border-white shadow-[0_60px_120px_rgba(0,0,0,0.05)] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[100px] rounded-full -mr-32 -mt-32" />
            <h2 className="text-5xl md:text-7xl lg:text-[8rem] font-black text-white mb-14 tracking-tighter uppercase leading-[0.85] px-4">Evolve Your <br /><span className="text-gradient">Legacy.</span></h2>
            <div className="flex flex-col sm:flex-row gap-8 justify-center">
              <a href="https://wa.me/94723954658" className="px-16 py-7 bg-blue-600 text-white font-black rounded-[2.5rem] hover:bg-blue-700 transition-all shadow-[0_25px_50px_rgba(37,99,235,0.25)] hover:scale-105 active:scale-95 uppercase tracking-[0.2em] text-[11px]">INITIATE SPRINT</a>
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="px-16 py-7 bg-white/5 backdrop-blur-xl border border-white/10 text-white font-black rounded-[2.5rem] hover:bg-white/10 transition-all uppercase tracking-[0.2em] text-[11px]">TERMINATE SESSION</button>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-32 px-6 border-t border-white/5 bg-slate-900/40 relative z-20">
        <div className="container mx-auto max-w-7xl flex flex-col md:flex-row justify-between items-center gap-12 text-[10px] font-black text-slate-300 uppercase tracking-widest">
          <p>© 2024 LAB Developers — Precision Engineering.</p>
          <div className="flex gap-10">
            <span className="hover:text-blue-600 cursor-pointer transition-colors">Protocol</span>
            <span className="hover:text-blue-600 cursor-pointer transition-colors">Compliance</span>
            <span className="hover:text-blue-600 cursor-pointer transition-colors">Encryption</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
